/**
 * ConnectIdeModal — správa MCP tokenů pro externí IDE napojené na jeden
 * Codable projekt. Token se vydá přes codable.token_create, jde ho volat
 * proti kanonickému veřejnému DEV endpointu projektu. Node-wide ani interní
 * fallback se pro projektový credential nepoužívá.
 *
 * Filip 2026-07-08: "klíč platí pro konkrétní projekt, jde ho volat přes URL
 * ACM MCP i samostatně" — proto neinventujeme nový token type, jen wrapper
 * kolem existující api_keys tabulky.
 */
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Check, Copy, Globe, KeyRound, Plus, Trash2, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { BackButton, Button, Select } from '../../../components/ui';
import { IdeOneClickOnboarding } from '../../../components/IdeOneClickOnboarding';
import {
  IDE_META,
  buildIdePack,
  downloadTextFile,
  type IdeClient,
  type IdePack,
} from '../../../lib/ide-onboarding';
import { themedConfirm } from '../../../lib/themed-dialogs';

interface TokenRow {
  id: string;
  label: string;
  key_preview: string;
  client_type: string;
  enabled: boolean;
  expires_at: string | null;
  last_used: string | null;
  use_count: number;
  created_at: string;
}

interface McpPublicationState {
  domain: string;
  url: string | null;
  desired: boolean;
  actual: boolean;
  published: boolean;
}

interface McpEndpoint {
  kind: 'project' | 'project-pending' | 'project-disabled';
  url: string | null;
  requested_url: string;
  enabled: boolean;
  project: McpPublicationState;
}

interface Props {
  slug: string;
  orchestratorProjectId: string | null;
  onClose: () => void;
  onBack?: () => void;
}

export default function ConnectIdeModal(
  { slug, orchestratorProjectId, onClose, onBack }: Props,
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const serverName = `codable-${slug}`;
  const [label, setLabel] = useState('');
  const [expiresDays, setExpiresDays] = useState<number | null>(null);
  const [idePack, setIdePack] = useState<IdePack | null>(null);
  const [newToken, setNewToken] = useState<
    null | {
      plaintext: string;
      snippet: string;
      mcp_url: string;
      scopes: string[];
      mcp_endpoint?: McpEndpoint;
    }
  >(null);

  const tokensQ = useQuery({
    queryKey: ['codable-tokens', slug],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        tokens: TokenRow[];
        mcp_url?: string | null;
        mcp_endpoint?: McpEndpoint;
        mcp_ready?: boolean;
      }>(
        'codable.token_list',
        { slug },
      ),
  });

  const createM = useMutation({
    mutationFn: ({ clientType, keyLabel }: { clientType: string; keyLabel: string }) =>
      api.mcp<{
        ok: boolean;
        error?: string;
        plaintext?: string;
        snippet?: string;
        mcp_url?: string;
        mcp_endpoint?: McpEndpoint;
        scopes?: string[];
      }>('codable.token_create', {
        slug,
        client_type: clientType,
        label: keyLabel || undefined,
        expires_days: expiresDays ?? undefined,
      }),
    onError: (e: unknown) => toast.error(String((e as Error).message)),
  });

  const mcpPubM = useMutation({
    mutationFn: () => {
      if (!orchestratorProjectId) {
        throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
      }
      return api.mcp<{
        ok: boolean;
        error?: string;
        mcp_url?: string;
        requested_url?: string;
        published?: boolean;
      }>(
        `${orchestratorProjectId}.orchestrator.mcp-publish`,
        {},
      );
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.ideMcpPublishFail'));
        return;
      }
      toast.success(
        r.published ? t('codable.ideMcpPublished', { url: r.mcp_url ?? '' }) : t(
          'codable.ideMcpPublishRequested',
          'Publikace byla vyžádaná; čeká se na proxy a certifikát ({{url}}).',
          { url: r.requested_url ?? '' },
        ),
      );
      qc.invalidateQueries({ queryKey: ['codable-tokens', slug] });
    },
    onError: (e: unknown) => toast.error(String(e)),
  });

  const mcpUnpubM = useMutation({
    mutationFn: () => {
      if (!orchestratorProjectId) throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
      return api.mcp<{ ok: boolean; error?: string }>(
        `${orchestratorProjectId}.orchestrator.mcp-unpublish`,
        {},
      );
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.ideMcpDisableFail', 'Vypnutí MCP selhalo'));
        return;
      }
      toast.success(t('codable.ideMcpDisabled', 'Projektové MCP bylo vypnuto; DEV náhled zůstal aktivní.'));
      qc.invalidateQueries({ queryKey: ['codable-tokens', slug] });
    },
    onError: (e: unknown) => toast.error(String(e)),
  });

  const revokeM = useMutation({
    mutationFn: (key_id: string) =>
      api.mcp<{ ok: boolean; error?: string }>('codable.token_revoke', {
        key_id,
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.ideRevokeError'));
        return;
      }
      toast.success(t('codable.ideTokenRevoked'));
      qc.invalidateQueries({ queryKey: ['codable-tokens', slug] });
    },
  });

  const copy = (v: string, msg = t('codable.ideCopied')) => {
    navigator.clipboard.writeText(v).then(() => toast.success(msg)).catch(() =>
      toast.error(t('codable.ideCopyFail'))
    );
  };

  const endpoint = tokensQ.data?.mcp_endpoint;
  const selectedMcpUrl = endpoint?.url ?? tokensQ.data?.mcp_url ?? null;
  const dedicatedPublished = endpoint?.kind === 'project' && tokensQ.data?.mcp_ready === true;
  const dedicatedPending = endpoint?.kind === 'project-pending' ||
    (endpoint?.kind === 'project' && tokensQ.data?.mcp_ready !== true);
  const dedicatedDisabled = endpoint?.kind === 'project-disabled';
  const selectedEndpointLabel = t(
    'codable.ideMcpProjectSelected',
    'Veřejný DEV MCP endpoint projektu',
  );

  const packFor = (ide: IdeClient, plaintext: string, url: string): IdePack => {
    const prj = orchestratorProjectId;
    const protocolNote = prj
      ? t('connect.projectProtocolNote', { prj })
      : undefined;
    const base = buildIdePack(ide, url, plaintext, {
      serverName,
      projectSlug: slug,
      projectId: prj,
      protocolNote,
    });
    const meta = IDE_META[ide];
    const when = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const toolHint = prj ? `${prj}.codable.files` : `codable.files (slug ${slug})`;
    const doc = t(
      'connect.onboardingDocProject',
      `# Codable — {{ideLabel}} · {{slug}}

Vygenerováno {{when}}. Klíč platí jen pro tento projekt.

## Nejrychlejší cesta
Otevři odkaz — {{ideLabel}} se spustí a MCP si vyplní sám:

    {{deeplink}}

## MCP
- **URL:** {{mcpUrl}}
- **API klíč:** \`{{key}}\` (plaintext ACM znovu neukáže)

## Co agent smí
Jen tento projekt:
- \`{{toolHint}}\` a další \`{{prjPrefix}}codable.*\`
- lifecycle: \`{{prjPrefix}}orchestrator.*\`

Nesmí platformní admin, cizí projekty, ani volání bez prefixu projektu.

## Jak zadávat práci
1. Strom / hledání → čtení → zápis → status → commit
2. Cesty relativně ke kořeni repa (ne \`src/src\`)
3. Binárky: encoding=base64

Vyzkoušej: „vypiš soubory projektu“ nebo „zavolej {{toolHint}}“.

## Ruční konfigurace ({{configPath}})
\`\`\`json
{{snippet}}
\`\`\`

## Grok / Claude konektor
Do konektoru patří MCP adresa výše. OAuth si najde na \`/mcp/oauth\`. Tento API klíč je pro IDE.

## Správa
V projektu → Připojit IDE → Revoke.
`,
      {
        ideLabel: meta.label,
        slug,
        when,
        deeplink: base.deeplink,
        mcpUrl: url,
        key: plaintext,
        snippet: base.snippet,
        configPath: meta.configPath,
        toolHint,
        prjPrefix: prj ? `${prj}.` : '',
      },
    );
    return { ...base, doc };
  };

  const applyFreshToken = (
    r: {
      plaintext: string;
      snippet?: string;
      mcp_url?: string;
      mcp_endpoint?: McpEndpoint;
      scopes?: string[];
    },
    ide: IdeClient,
  ) => {
    const url = r.mcp_url ?? selectedMcpUrl ?? '';
    const pack = packFor(ide, r.plaintext, url);
    setNewToken({
      plaintext: r.plaintext,
      snippet: pack.snippet,
      mcp_url: url,
      mcp_endpoint: r.mcp_endpoint,
      scopes: r.scopes ?? [],
    });
    setIdePack(pack);
    qc.invalidateQueries({ queryKey: ['codable-tokens', slug] });
    qc.invalidateQueries({ queryKey: ['acm-connect-tokens', 'project', slug] });
    return pack;
  };

  const mintToken = async (ide: IdeClient) => {
    if (newToken) {
      return { plaintext: newToken.plaintext, mcp_url: newToken.mcp_url, snippet: newToken.snippet };
    }
    const r = await createM.mutateAsync({
      clientType: ide,
      keyLabel: label.trim() || IDE_META[ide].defaultLabel,
    });
    if (!r.ok || !r.plaintext) {
      toast.error(r.error ?? t('codable.ideCreateError'));
      return null;
    }
    return r;
  };

  const onConnectIde = async (ide: IdeClient) => {
    const minted = await mintToken(ide);
    if (!minted?.plaintext) return;
    const pack = applyFreshToken(
      {
        plaintext: minted.plaintext,
        snippet: minted.snippet,
        mcp_url: minted.mcp_url,
      },
      ide,
    );
    downloadTextFile(pack.filename, pack.doc);
    window.location.href = pack.deeplink;
  };

  return (
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4'>
      <div className='bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto space-y-5'>
        <div className='flex items-center justify-between gap-3'>
          {onBack
            ? (
              <BackButton
                onClick={onBack}
                label={t('codable.wsSettings')}
                crumb={t('codable.ideTitleFull')}
              />
            )
            : (
              <h3 className='font-bold text-white text-lg flex items-center gap-2 min-w-0'>
                <KeyRound size={20} className='text-blue-400 shrink-0' />
                {t('codable.ideTitleFull')} <span className='font-mono text-gray-400'>{slug}</span>
              </h3>
            )}
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-200 shrink-0'
          >
            <X size={20} />
          </button>
        </div>

        <div className='bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-gray-400 leading-relaxed'>
          {t(
            'codable.ideTokenScopeHint',
            'Klíč platí jen pro tento Codable projekt. Agent volá {{prj}}.codable.* — ne celou platformu.',
            { prj: orchestratorProjectId ?? slug },
          )}{' '}
          <code className='font-mono text-blue-300'>
            codable:project:{slug}
          </code>
        </div>

        {
          /* MCP endpoint je přesná /mcp cesta veřejné DEV URL projektu.
            Node-wide ani interní fallback do IDE konfigurace nepatří. */
        }
        <div className='space-y-2'>
          {selectedMcpUrl && (
            <div>
              <div className='text-xs text-gray-500 mb-1'>{selectedEndpointLabel}</div>
              <div className='flex items-center gap-2'>
                <code
                  className={`flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs font-mono break-all ${
                    dedicatedPublished ? 'text-emerald-300' : 'text-blue-300'
                  }`}
                >
                  {selectedMcpUrl}
                </code>
                <Button
                  size='sm'
                  variant='ghost'
                  icon={<Copy size={14} />}
                  onClick={() => copy(selectedMcpUrl, t('codable.ideMcpUrlCopied'))}
                />
              </div>
            </div>
          )}
          {!selectedMcpUrl && endpoint?.requested_url && (
            <code className='block bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-gray-500 font-mono break-all'>
              {endpoint.requested_url}
            </code>
          )}
          {dedicatedPublished && (
            <div className='flex justify-end'>
              <Button
                size='sm'
                variant='ghost'
                loading={mcpUnpubM.isPending}
                onClick={() => mcpUnpubM.mutate()}
              >
                {t('codable.ideDisableMcp', 'Vypnout MCP (náhled zůstane)')}
              </Button>
            </div>
          )}
          {!dedicatedPublished && (
            <div className='border border-amber-500/25 bg-amber-950/10 rounded-lg p-3 space-y-2'>
              <div className='text-xs text-amber-300'>
                {dedicatedPending
                  ? t(
                    'codable.ideMcpPending',
                    'Veřejná DEV URL nebo její /mcp cesta ještě nejsou potvrzené.',
                  )
                  : t(
                    'codable.ideMcpDisabledHint',
                    'MCP je pro tento projekt vypnuté. DEV náhled zůstává veřejně dostupný.',
                  )}
              </div>
              {endpoint?.project.domain && (
                <code className='block text-[11px] text-gray-500 font-mono break-all'>
                  {endpoint.project.domain} · desired={String(endpoint.project.desired)}{' '}
                  · actual={String(endpoint.project.actual)}
                </code>
              )}
              <Button
                size='sm'
                variant='primary'
                icon={<Globe size={14} />}
                loading={mcpPubM.isPending}
                onClick={() => mcpPubM.mutate()}
              >
                {dedicatedPending
                  ? t('codable.ideReconcileMcp', 'Ověřit / dorovnat publikaci')
                  : dedicatedDisabled
                  ? t('codable.ideEnableMcp', 'Zapnout MCP na DEV URL')
                  : t('codable.idePublishMcp')}
              </Button>
            </div>
          )}
        </div>

        <IdeOneClickOnboarding
          disabled={!dedicatedPublished}
          creating={createM.isPending}
          label={label}
          onLabelChange={setLabel}
          onConnect={(ide) => void onConnectIde(ide)}
          canOpenOrDownload={!!idePack}
          activeIde={idePack?.ide ?? null}
          title={t('connect.quickTitleProject', 'One-click do IDE')}
          hint={t(
            'connect.quickHintProject',
            'VS Code, VSCodium, Antigravity nebo Cursor: vytvoří klíč jen pro tento projekt, stáhne Codable návod a otevře IDE s MCP. Grok/Claude konektor klíč nepotřebuje — stačí MCP adresa.',
          )}
          onOpenOnly={() => {
            if (idePack) window.location.href = idePack.deeplink;
          }}
          onDownloadOnly={() => {
            if (idePack) downloadTextFile(idePack.filename, idePack.doc);
          }}
        />

        {/* Freshly created token — plaintext se ukáže jen jednou */}
        {newToken && (
          <div className='border border-emerald-500/40 bg-emerald-500/5 rounded-lg p-4 space-y-3'>
            <div className='flex items-center gap-2 text-emerald-300 font-medium text-sm'>
              <Check size={16} /> {t('codable.ideTokenCreated')}
            </div>

            <div>
              <div className='text-xs text-gray-500 mb-1'>
                {t('codable.ideApiKeyLabel')}
              </div>
              <div className='flex items-center gap-2'>
                <code className='flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-emerald-300 font-mono break-all'>
                  {newToken.plaintext}
                </code>
                <Button
                  size='sm'
                  variant='ghost'
                  icon={<Copy size={14} />}
                  onClick={() => copy(newToken.plaintext, t('codable.ideKeyCopied'))}
                />
              </div>
            </div>

            <div>
              <div className='text-xs text-gray-500 mb-1'>
                {newToken.mcp_endpoint?.kind === 'project'
                  ? t('codable.ideMcpProjectSelected', 'Dedikovaný MCP endpoint projektu')
                  : t('codable.ideMcpEndpointLabel')}
              </div>
              <div className='flex items-center gap-2'>
                <code className='flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-gray-300 font-mono break-all'>
                  {newToken.mcp_url}
                </code>
                <Button
                  size='sm'
                  variant='ghost'
                  icon={<Copy size={14} />}
                  onClick={() => copy(newToken.mcp_url)}
                />
              </div>
            </div>

            <div>
              <div className='flex items-center justify-between mb-1'>
                <div className='text-xs text-gray-500'>
                  {idePack ? IDE_META[idePack.ide].label : t('codable.ideCustom')} config snippet
                </div>
                <button
                  onClick={() => copy(newToken.snippet, t('codable.ideSnippetCopied'))}
                  className='text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1'
                >
                  <Copy size={12} /> {t('codable.ideCopySnippet')}
                </button>
              </div>
              <pre className='bg-gray-950 border border-gray-800 rounded p-2 text-[11px] text-gray-300 font-mono overflow-x-auto max-h-64'>
{newToken.snippet}
              </pre>
            </div>

            <div className='text-xs text-amber-400 flex items-start gap-2'>
              <AlertTriangle size={14} className='shrink-0 mt-0.5' />
              <span>
                {t('codable.ideSelfSignedHint')}
              </span>
            </div>

            <div className='flex justify-end'>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => setNewToken(null)}
              >
                {t('codable.ideDoneSaved')}
              </Button>
            </div>
          </div>
        )}

        {/* Existing tokens list */}
        <section>
          <h4 className='text-xs uppercase font-bold text-gray-500 mb-2'>
            {t('codable.ideExistingTokens')}
          </h4>
          {tokensQ.isLoading && (
            <div className='text-gray-500 text-sm'>
              {t('codable.ideLoading')}
            </div>
          )}
          {tokensQ.data?.tokens?.length === 0 && (
            <div className='text-gray-500 text-sm italic'>
              {t('codable.ideNoTokens')}
            </div>
          )}
          <div className='space-y-1.5'>
            {(tokensQ.data?.tokens ?? []).map((tok) => (
              <div
                key={tok.id}
                className={`border rounded-lg p-2.5 flex items-center gap-2 text-sm ${
                  tok.enabled
                    ? 'border-gray-800 bg-gray-950'
                    : 'border-gray-800 bg-gray-950/50 opacity-60'
                }`}
              >
                <div className='flex-1 min-w-0'>
                  <div className='text-gray-200 truncate'>{tok.label}</div>
                  <div className='text-[11px] text-gray-500 font-mono'>
                    {tok.key_preview}… · {tok.client_type} · use={tok.use_count}
                    {tok.expires_at
                      ? ` · expires ${new Date(tok.expires_at).toLocaleDateString()}`
                      : ` · ${t('codable.idePersistent')}`}
                    {tok.last_used
                      ? ` · last ${new Date(tok.last_used).toLocaleDateString()}`
                      : ` · ${t('codable.ideNever')}`}
                  </div>
                </div>
                {tok.enabled
                  ? (
                    <button
                      onClick={async () => {
                        if (
                          await themedConfirm(
                            t('codable.ideRevokeConfirm', { label: tok.label }),
                          )
                        ) {
                          revokeM.mutate(tok.id);
                        }
                      }}
                      className='text-gray-500 hover:text-red-400'
                      title={t('codable.ideRevoke')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )
                  : (
                    <span className='text-[10px] uppercase text-gray-600'>
                      revoked
                    </span>
                  )}
              </div>
            ))}
          </div>
        </section>

        {/* Create form */}
        <section className='border-t border-gray-800 pt-4'>
          <h4 className='text-xs uppercase font-bold text-gray-500 mb-3'>
            {t('codable.ideGenerateNew')}
          </h4>
          <div className='grid grid-cols-2 gap-2 mb-3'>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('codable.ideLabelOptional')}
              </label>
              <input
                className='w-full bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('codable.ideLabelPh')}
              />
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('codable.ideExpiry')}
              </label>
              <Select
                className='w-full'
                value={expiresDays ?? ''}
                onChange={(e) =>
                  setExpiresDays(
                    e.target.value === '' ? null : Number(e.target.value),
                  )}
              >
                <option value=''>{t('codable.idePersistent')}</option>
                <option value='30'>{t('codable.ideDays30')}</option>
                <option value='90'>{t('codable.ideDays90')}</option>
                <option value='365'>{t('codable.ideYear1')}</option>
              </Select>
            </div>
          </div>
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={14} />}
            loading={createM.isPending}
            disabled={!dedicatedPublished}
            onClick={() => {
              void (async () => {
                const r = await createM.mutateAsync({
                  clientType: 'generic',
                  keyLabel: label.trim() || t('connect.manualKeyLabel', 'osobní token'),
                });
                if (!r.ok || !r.plaintext) {
                  toast.error(r.error ?? t('codable.ideCreateError'));
                  return;
                }
                applyFreshToken(
                  {
                    plaintext: r.plaintext,
                    snippet: r.snippet,
                    mcp_url: r.mcp_url,
                    mcp_endpoint: r.mcp_endpoint,
                    scopes: r.scopes,
                  },
                  idePack?.ide ?? 'vscode',
                );
              })();
            }}
          >
            {t('codable.ideCreateToken')}
          </Button>
        </section>
      </div>
    </div>
  );
}
